// src/components/goal-settings/KpiEditModal.jsx
import React, { useEffect, useState } from 'react';

export default function KpiEditModal({
                                         show,
                                         kpi,
                                         onClose,
                                         onSaved, // (patch) -> parent calls updateKpi
                                     }) {
    const isOpen = !!show;
    const [name, setName] = useState('');
    const [threshold, setThreshold] = useState('');
    const [target, setTarget] = useState('');
    const [stretch, setStretch] = useState('');
    const [weight, setWeight] = useState('');
    const [isPercentage, setIsPercentage] = useState(false);
    const [unit, setUnit] = useState('');
    const [higherIsBetter, setHigherIsBetter] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (!isOpen || !kpi) return;
        setName(kpi.name ?? '');
        setThreshold(kpi.threshold ?? '');
        setTarget(kpi.target ?? '');
        setStretch(kpi.stretch ?? '');
        setWeight(kpi.weight ?? '');
        setIsPercentage(!!kpi.is_percentage);
        setUnit(kpi.unit ?? '');
        setHigherIsBetter(kpi.higher_is_better !== false);
        setErr('');
    }, [isOpen, kpi]);

    if (!isOpen) return null;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim()) return setErr('Name is required.');
        if (!weight || !Number.isFinite(Number(weight))) return setErr('Weight must be a number.');

        try {
            setSaving(true);
            setErr('');

            const patch = {
                name: name.trim(),
                threshold: threshold !== '' ? Number(threshold) : null,
                target:    target    !== '' ? Number(target)    : null,
                stretch:   stretch   !== '' ? Number(stretch)   : null,
                weight: Number(weight),
                is_percentage: !!isPercentage,
                unit: isPercentage ? null : (unit?.trim() || null),
                higher_is_better: !!higherIsBetter,
            };

            await onSaved?.(patch);
            onClose?.();
        } catch (e2) {
            setErr(e2?.response?.data?.message || 'Failed to update KPI.');
        } finally {
            setSaving(false);
        }
    };

    const cascaded = Boolean(kpi?.parent_id);

    return (
        <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,.4)' }} aria-modal="true" role="dialog">
            <div className="modal-dialog modal-md">
                <div className="modal-content">
                    <div className="modal-header py-2">
                        <h6 className="modal-title">Edit KPI  {cascaded && <span className="badge bg-secondary ms-2">Cascaded</span>}</h6>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <form onSubmit={submit}>
                        <div className="modal-body">
                            {err && <div className="alert alert-danger py-1">{err}</div>}

                            <div className="mb-2">
                                <label className="form-label mb-1">Name <span className="text-danger">*</span></label>
                                <input className="form-control form-control-sm" value={name} onChange={e => setName(e.target.value)} autoFocus/>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <label className="form-label mb-1">Threshold</label>
                                    <input className="form-control form-control-sm" type="number" step="0.0001" value={threshold} onChange={e => setThreshold(e.target.value)} />
                                </div>
                                <div className="col-6">
                                    <label className="form-label mb-1">Target</label>
                                    <input className="form-control form-control-sm" type="number" step="0.0001" value={target} onChange={e => setTarget(e.target.value)} />
                                </div>
                            </div>

                            <div className="row mt-1">
                                <div className="col-6">
                                    <label className="form-label mb-1">Stretch</label>
                                    <input className="form-control form-control-sm" type="number" step="0.0001" value={stretch} onChange={e => setStretch(e.target.value)} />
                                </div>
                                <div className="col-6">
                                    <label className="form-label mb-1">Weight <span className="text-danger">*</span></label>
                                    <input className="form-control form-control-sm" type="number" step="0.0001" value={weight} onChange={e => setWeight(e.target.value)} />
                                </div>
                            </div>

                            <div className="form-check mt-2">
                                <input className="form-check-input" type="checkbox" id="kpiIsPctEdit" checked={isPercentage} onChange={e => setIsPercentage(e.target.checked)} />
                                <label className="form-check-label" htmlFor="kpiIsPctEdit">Percentage</label>
                            </div>

                            <div className="mt-2">
                                <label className="form-label mb-1">Unit</label>
                                <input className="form-control form-control-sm" value={unit} onChange={e => setUnit(e.target.value)} disabled={isPercentage} placeholder={isPercentage ? 'Disabled when %' : 'e.g., kg, items, days'} />
                            </div>

                            <div className="form-check mt-2">
                                <input className="form-check-input" type="checkbox" id="kpiHigherBetterEdit" checked={higherIsBetter} onChange={e => setHigherIsBetter(e.target.checked)} />
                                <label className="form-check-label" htmlFor="kpiHigherBetterEdit">
                                    Higher is better
                                    <div className="text-muted small">
                                        When on: higher actuals score better; when off: lower actuals score better.
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="modal-footer py-2">
                            <button type="button" className="btn btn-sm btn-light" onClick={onClose} disabled={saving}>Cancel</button>
                            <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                                {saving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
